import { createContext, FunctionComponent } from 'preact';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'preact/hooks';

import { createFloatingTrackEvent } from '../services/tracking';
import { useConfig } from './ConfigProvider';
import { useCurrentAsset } from './CurrentAssetProvider';
import { usePlayer } from './PlayerProvider';
import { usePulse } from './PulseProvider';
import { usePlayerContainer } from './usePlayerContainer';
import { usePlayerEvent } from './usePlayerEvent';
import { useProvider } from './useProvider';
import {
  useFloatingAspectRatio,
  useViewportWidth,
} from './useFloatingAspectRatio';
import {
  applyCssVars,
  computeFloatingCssVars,
  resolveFloatingConfig,
  resolveFloatingConstraints,
  resolveFloatingDimensions,
} from './floatingLayout';

interface FloatingContextProps {
  isFloating: boolean;
}

const FloatingContext = createContext<FloatingContextProps>(undefined!);

const getFloatingSafe = (player: ReturnType<typeof usePlayer>) => {
  try {
    return player.getFloating();
  } catch {
    return false;
  }
};

export const FloatingProvider: FunctionComponent = ({ children }) => {
  const player = usePlayer();
  const { asset } = useCurrentAsset();
  const container = usePlayerContainer();
  const { floating } = useConfig();
  const { configProvider: provider } = useProvider();
  const pulse = usePulse();

  const resolved = useMemo(() => resolveFloatingConfig(floating), [floating]);
  const aspectRatio = useFloatingAspectRatio(asset);
  const viewportWidth = useViewportWidth();

  const [isFloating, setIsFloating] = useState(getFloatingSafe(player));
  const [isAd, setIsAd] = useState(false);

  const isAdRef = useRef(false);
  const adIdRef = useRef<string | undefined>();

  const trackFloatingEvent = useCallback(
    (isFloating: boolean) => {
      const position = Math.floor(player.getCurrentTime());
      const duration = Math.floor(player.getDuration() * 1000);

      const trackEvent = createFloatingTrackEvent({
        provider,
        asset: asset ?? {},
        isFloating,
        isAd: isAdRef.current,
        adId: adIdRef.current,
        position,
        duration,
      });

      pulse.trackMediaEvent(trackEvent);
    },
    [player, provider, asset, pulse]
  );

  const handleFloat = useCallback(({ floating }: { floating: boolean }) => {
    setIsFloating(floating);
    trackFloatingEvent(floating);
  }, []);
  usePlayerEvent('float', handleFloat);

  const handleAdStarted = useCallback((param: { meta: { adId?: string } }) => {
    setIsAd(true);
    isAdRef.current = true;
    adIdRef.current = param.meta.adId;
  }, []);
  const handleAdComplete = useCallback(() => {
    setIsAd(false);
    isAdRef.current = false;
    adIdRef.current = undefined;
  }, []);
  usePlayerEvent('adStarted', handleAdStarted);
  usePlayerEvent('adSlotComplete', handleAdComplete);

  useEffect(() => {
    if (!asset && !isFloating) {
      return;
    }
    const isMobile = viewportWidth < resolved.mobileBreakpoint;
    const isPortrait = aspectRatio < 1;

    const { maxWidth, maxHeight } = resolveFloatingConstraints({
      resolved,
      isMobile,
      isPortrait,
      isAd,
    });

    const { width, height } = resolveFloatingDimensions({
      aspectRatio,
      isMobile,
      viewportWidth,
      offset: resolved.offset,
      maxWidth,
      maxHeight,
    });

    const [desktopBlock, desktopInline] = resolved.position.split('-');
    const vars = computeFloatingCssVars({
      width,
      height,
      offset: resolved.offset,
      blockPosition: (isMobile ? resolved.mobilePosition : desktopBlock) as
        'top' | 'bottom',
      inlinePosition: (isMobile
        ? resolved.mobileVerticalAlign
        : desktopInline) as 'left' | 'right',
    });
    applyCssVars(container, vars);
  }, [
    asset,
    container,
    isFloating,
    isAd,
    aspectRatio,
    viewportWidth,
    resolved,
  ]);

  const state = useMemo(() => ({ isFloating }), [isFloating]);

  return (
    <FloatingContext.Provider value={state}>
      {children}
    </FloatingContext.Provider>
  );
};

export const useFloating = () => {
  const context = useContext(FloatingContext);

  if (process.env.NODE_ENV !== 'production' && !context) {
    console.warn(
      'Floating context is undefined, please verify you are calling useFloating() as child of a <FloatingProvider> component.'
    );
  }

  return context;
};
